import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useAuth } from '../../contexts/AuthContext'
import { useFamily } from '../../contexts/FamilyContext'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import Card from '../ui/Card'
import Badge from '../ui/Badge'
import Button from '../ui/Button'
import ProgressBar from '../ui/ProgressBar'

const {
  FiBarChart3,
  FiTrendingUp,
  FiUsers,
  FiStar,
  FiClock,
  FiCalendar,
  FiTarget,
  FiAward,
  FiActivity,
  FiRefreshCw,
  FiDatabase,
  FiAlertCircle
} = FiIcons

const AnalyticsView = () => {
  const { family } = useAuth()
  const { tasks, completions, rewards, getChildPoints, loading } = useFamily()
  const [analytics, setAnalytics] = useState(null)
  const [analyticsLoading, setAnalyticsLoading] = useState(true)
  const [timeRange, setTimeRange] = useState(30)
  const [debugInfo, setDebugInfo] = useState(null)

  // Debug function to check data availability
  const checkDataAvailability = () => {
    const debug = {
      family: {
        exists: !!family,
        id: family?.id,
        name: family?.name,
        childrenCount: family?.children?.length || 0
      },
      tasks: {
        count: tasks.length,
        sample: tasks.slice(0, 2).map(t => ({ id: t.id, title: t.title, category: t.category }))
      },
      completions: {
        count: completions.length,
        sample: completions.slice(0, 2).map(c => ({ 
          id: c.id, 
          task_id: c.task_id, 
          child_id: c.child_id, 
          points_earned: c.points_earned,
          completed_at: c.completed_at 
        }))
      },
      rewards: {
        count: rewards.length,
        sample: rewards.slice(0, 2).map(r => ({ id: r.id, title: r.title, points_required: r.points_required }))
      },
      loading: loading
    }
    
    console.log('Analytics Debug Info:', debug)
    setDebugInfo(debug)
    return debug
  }

  useEffect(() => {
    if (family?.id && !loading) {
      loadAnalytics()
    }
  }, [family?.id, timeRange, tasks, completions, loading])

  const loadAnalytics = async () => {
    try {
      console.log('🔍 Loading analytics for family:', family.id)
      
      // Check data availability first
      const debugData = checkDataAvailability()
      
      setAnalyticsLoading(true)
      
      const now = new Date()
      const startDate = new Date(now.getTime() - (timeRange * 24 * 60 * 60 * 1000))

      // Filter completions within time range
      const recentCompletions = completions.filter(completion => {
        if (!completion.completed_at) return false
        return new Date(completion.completed_at) >= startDate
      })

      console.log('📊 Data Summary:', {
        totalTasks: tasks.length,
        totalCompletions: completions.length,
        recentCompletions: recentCompletions.length,
        timeRangeDays: timeRange,
        familyChildren: family?.children?.length || 0
      })

      // Calculate basic stats
      const totalTasksCompleted = recentCompletions.length
      const totalPointsEarned = recentCompletions.reduce((sum, c) => sum + (c.points_earned || 0), 0)

      // Calculate completion rate based on available tasks vs completions
      const activeTasks = tasks.filter(task => task.is_active !== false)
      const completionRate = activeTasks.length > 0 
        ? Math.round((totalTasksCompleted / Math.max(activeTasks.length, 1)) * 100) 
        : 0

      // Find most active child
      const childActivity = {}
      recentCompletions.forEach(completion => {
        const childId = completion.child_id
        if (childId) {
          childActivity[childId] = (childActivity[childId] || 0) + 1
        }
      })

      const mostActiveChildId = Object.keys(childActivity).length > 0 
        ? Object.keys(childActivity).reduce((a, b) => childActivity[a] > childActivity[b] ? a : b)
        : null
      
      const mostActiveChild = mostActiveChildId 
        ? family?.children?.find(c => c.id === mostActiveChildId)
        : null

      // Calculate total lifetime stats for better context
      const lifetimeTasksCompleted = completions.length
      const lifetimePointsEarned = completions.reduce((sum, c) => sum + (c.points_earned || 0), 0)

      // Calculate analytics data
      const analyticsData = {
        total_tasks_completed: totalTasksCompleted,
        total_points_earned: totalPointsEarned,
        completion_rate: Math.min(completionRate, 100), // Cap at 100%
        most_active_child_name: mostActiveChild?.name || null,
        most_active_child_avatar: mostActiveChild?.avatar || null,
        active_children: family?.children?.length || 0,
        total_tasks_available: activeTasks.length,
        timeframe: timeRange,
        // Add lifetime stats for context
        lifetime_tasks_completed: lifetimeTasksCompleted,
        lifetime_points_earned: lifetimePointsEarned,
        // Add flags to help determine if we have meaningful data
        has_children: (family?.children?.length || 0) > 0,
        has_tasks: activeTasks.length > 0,
        has_completions: lifetimeTasksCompleted > 0,
        has_recent_activity: totalTasksCompleted > 0,
        // Add debug info
        debug_info: debugData
      }

      console.log('✅ Analytics calculated:', analyticsData)
      setAnalytics(analyticsData)
    } catch (error) {
      console.error('❌ Analytics error:', error)
      // Set fallback data even on error
      const fallbackData = {
        total_tasks_completed: 0,
        total_points_earned: 0,
        completion_rate: 0,
        most_active_child_name: null,
        most_active_child_avatar: null,
        active_children: family?.children?.length || 0,
        total_tasks_available: tasks.length,
        timeframe: timeRange,
        lifetime_tasks_completed: completions.length,
        lifetime_points_earned: completions.reduce((sum, c) => sum + (c.points_earned || 0), 0),
        has_children: (family?.children?.length || 0) > 0,
        has_tasks: tasks.length > 0,
        has_completions: completions.length > 0,
        has_recent_activity: false,
        debug_info: checkDataAvailability()
      }
      setAnalytics(fallbackData)
    } finally {
      setAnalyticsLoading(false)
    }
  }

  const getTopPerformer = () => {
    if (!family?.children || family.children.length === 0) return null

    let topChild = null
    let maxPoints = 0

    family.children.forEach(child => {
      const points = getChildPoints(child.id)
      if (points > maxPoints) {
        maxPoints = points
        topChild = child
      }
    })

    return topChild ? { ...topChild, points: maxPoints } : null
  }

  const getCompletionTrend = () => {
    const last7Days = Array.from({ length: 7 }, (_, i) => {
      const date = new Date()
      date.setDate(date.getDate() - i)
      return {
        date: date.toDateString(),
        dateObj: date
      }
    }).reverse()

    return last7Days.map(({ date, dateObj }) => {
      const count = completions.filter(c => 
        c.completed_at && new Date(c.completed_at).toDateString() === date
      ).length
      return {
        date,
        count,
        dayName: dateObj.toLocaleDateString('en', { weekday: 'short' })
      }
    })
  }

  const getCategoryBreakdown = () => {
    const categories = {}
    completions.forEach(completion => {
      const task = tasks.find(t => t.id === completion.task_id)
      if (task && task.category) {
        categories[task.category] = (categories[task.category] || 0) + 1
      }
    })

    return Object.entries(categories)
      .map(([category, count]) => ({ category, count }))
      .sort((a, b) => b.count - a.count)
  }

  const getChildrenAnalytics = () => {
    if (!family?.children || family.children.length === 0) return []

    return family.children.map(child => {
      const childCompletions = completions.filter(c => c.child_id === child.id)
      const points = getChildPoints(child.id)

      // Find favorite category
      const childCategories = {}
      childCompletions.forEach(completion => {
        const task = tasks.find(t => t.id === completion.task_id)
        if (task && task.category) {
          childCategories[task.category] = (childCategories[task.category] || 0) + 1
        }
      })

      const favoriteCategory = Object.keys(childCategories).length > 0
        ? Object.entries(childCategories).sort(([, a], [, b]) => b - a)[0][0]
        : 'none'

      return {
        ...child,
        totalCompletions: childCompletions.length,
        totalPoints: points,
        favoriteCategory
      }
    })
  }

  const timeRangeOptions = [
    { value: 7, label: 'Last 7 days' },
    { value: 30, label: 'Last 30 days' },
    { value: 90, label: 'Last 3 months' }
  ]

  // Show loading state
  if (loading || analyticsLoading) {
    return (
      <Card>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Loading analytics...</p>
          </div>
        </div>
      </Card>
    )
  }

  // Show error state if no family
  if (!family) {
    return (
      <Card>
        <div className="text-center py-12">
          <div className="text-4xl mb-4">👨‍👩‍👧‍👦</div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
            No Family Data
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            Please complete your family setup to view analytics.
          </p>
        </div>
      </Card>
    )
  }

  // Debug section for troubleshooting
  if (debugInfo && (!analytics?.has_completions && !analytics?.has_tasks && !analytics?.has_children)) {
    return (
      <div className="space-y-6">
        {/* Debug Information Card */}
        <Card className="bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800">
          <div className="flex items-start gap-3 mb-4">
            <SafeIcon icon={FiAlertCircle} className="text-yellow-600 dark:text-yellow-400 text-lg mt-1" />
            <div>
              <h4 className="font-medium text-yellow-800 dark:text-yellow-200 mb-1">Analytics Debug Information</h4>
              <p className="text-sm text-yellow-700 dark:text-yellow-300">
                No data found for analytics. Here's what we can see:
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
            <div className="p-3 bg-white dark:bg-gray-800 rounded-lg border">
              <div className="font-medium text-gray-900 dark:text-gray-100 mb-1">Family</div>
              <div className="text-gray-600 dark:text-gray-400">
                <div>Exists: {debugInfo.family.exists ? '✅' : '❌'}</div>
                <div>ID: {debugInfo.family.id ? '✅' : '❌'}</div>
                <div>Children: {debugInfo.family.childrenCount}</div>
              </div>
            </div>

            <div className="p-3 bg-white dark:bg-gray-800 rounded-lg border">
              <div className="font-medium text-gray-900 dark:text-gray-100 mb-1">Tasks</div>
              <div className="text-gray-600 dark:text-gray-400">
                <div>Count: {debugInfo.tasks.count}</div>
                <div>Sample: {debugInfo.tasks.sample.length > 0 ? '✅' : '❌'}</div>
              </div>
            </div>

            <div className="p-3 bg-white dark:bg-gray-800 rounded-lg border">
              <div className="font-medium text-gray-900 dark:text-gray-100 mb-1">Completions</div>
              <div className="text-gray-600 dark:text-gray-400">
                <div>Count: {debugInfo.completions.count}</div>
                <div>Sample: {debugInfo.completions.sample.length > 0 ? '✅' : '❌'}</div>
              </div>
            </div>

            <div className="p-3 bg-white dark:bg-gray-800 rounded-lg border">
              <div className="font-medium text-gray-900 dark:text-gray-100 mb-1">Rewards</div>
              <div className="text-gray-600 dark:text-gray-400">
                <div>Count: {debugInfo.rewards.count}</div>
                <div>Sample: {debugInfo.rewards.sample.length > 0 ? '✅' : '❌'}</div>
              </div>
            </div>
          </div>

          <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <h5 className="font-medium text-blue-800 dark:text-blue-200 mb-2">Next Steps:</h5>
            <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
              <li>1. Go to the Tasks page and create some tasks</li>
              <li>2. Add children to your family if you haven't already</li>
              <li>3. Complete some tasks to generate completion data</li>
              <li>4. Return here to see your analytics</li>
            </ul>
          </div>

          <div className="mt-4 flex gap-2">
            <Button variant="outline" size="sm" onClick={loadAnalytics}>
              <SafeIcon icon={FiRefreshCw} className="text-sm" />
              Refresh Data
            </Button>
          </div>
        </Card>

        {/* Get Started Card */}
        <Card>
          <div className="text-center py-12">
            <div className="text-4xl mb-4">📊</div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Get Started with Analytics
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Create some tasks and start completing them to see detailed analytics about your family's progress!
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6 max-w-md mx-auto">
              <div className="p-4 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
                <div className="text-2xl mb-2">📝</div>
                <h4 className="font-medium text-primary-800 dark:text-primary-300">Create Tasks</h4>
                <p className="text-sm text-primary-600 dark:text-primary-400">Add household chores for your family</p>
              </div>
              <div className="p-4 bg-secondary-50 dark:bg-secondary-900/20 rounded-lg">
                <div className="text-2xl mb-2">⭐</div>
                <h4 className="font-medium text-secondary-800 dark:text-secondary-300">Complete Tasks</h4>
                <p className="text-sm text-secondary-600 dark:text-secondary-400">Start earning points and tracking progress</p>
              </div>
            </div>
          </div>
        </Card>
      </div>
    )
  }

  const topPerformer = getTopPerformer()
  const completionTrend = getCompletionTrend()
  const categoryBreakdown = getCategoryBreakdown()
  const childrenAnalytics = getChildrenAnalytics()

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <SafeIcon icon={FiBarChart3} className="text-primary-500" />
              Family Analytics
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Track your family's progress and achievements
            </p>
            {/* Show data summary */}
            <div className="flex items-center gap-4 mt-2 text-sm text-gray-500 dark:text-gray-400">
              <span>{analytics?.has_children ? `${analytics.active_children} children` : 'No children'}</span>
              <span>•</span>
              <span>{analytics?.has_tasks ? `${analytics.total_tasks_available} tasks` : 'No tasks'}</span>
              <span>•</span>
              <span>{analytics?.has_completions ? `${analytics.lifetime_tasks_completed} total completions` : 'No completions'}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(parseInt(e.target.value))}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            >
              {timeRangeOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <Button variant="ghost" size="sm" onClick={loadAnalytics}>
              <SafeIcon icon={FiRefreshCw} className="text-sm" />
            </Button>
          </div>
        </div>
      </Card>

      {/* Show message if no recent activity but there is historical data */}
      {!analytics?.has_recent_activity && analytics?.has_completions && (
        <Card className="bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800">
          <div className="flex items-start gap-3">
            <SafeIcon icon={FiClock} className="text-yellow-600 dark:text-yellow-400 text-lg mt-1" />
            <div>
              <h4 className="font-medium text-yellow-800 dark:text-yellow-200 mb-1">No Recent Activity</h4>
              <p className="text-sm text-yellow-700 dark:text-yellow-300">
                Your family has {analytics.lifetime_tasks_completed} total completions and {analytics.lifetime_points_earned} total points, 
                but no activity in the selected timeframe ({timeRange === 7 ? 'last 7 days' : timeRange === 30 ? 'last 30 days' : 'last 3 months'}). 
                Try selecting a longer time period or complete some tasks!
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="text-center">
            <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center mx-auto mb-3">
              <SafeIcon icon={FiTarget} className="text-white text-xl" />
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {analytics?.total_tasks_completed || 0}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Tasks Completed</div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {timeRange === 7 ? 'Last 7 days' : timeRange === 30 ? 'Last 30 days' : 'Last 3 months'}
            </div>
            {analytics?.lifetime_tasks_completed > analytics?.total_tasks_completed && (
              <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                ({analytics.lifetime_tasks_completed} total)
              </div>
            )}
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card className="text-center">
            <div className="w-12 h-12 bg-gradient-to-br from-success-500 to-success-600 rounded-xl flex items-center justify-center mx-auto mb-3">
              <SafeIcon icon={FiStar} className="text-white text-xl" />
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {analytics?.total_points_earned || 0}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Points Earned</div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {timeRange === 7 ? 'Last 7 days' : timeRange === 30 ? 'Last 30 days' : 'Last 3 months'}
            </div>
            {analytics?.lifetime_points_earned > analytics?.total_points_earned && (
              <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                ({analytics.lifetime_points_earned} total)
              </div>
            )}
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card className="text-center">
            <div className="w-12 h-12 bg-gradient-to-br from-secondary-500 to-secondary-600 rounded-xl flex items-center justify-center mx-auto mb-3">
              <SafeIcon icon={FiTrendingUp} className="text-white text-xl" />
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {analytics?.completion_rate || 0}%
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Activity Rate</div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Completions vs Available Tasks
            </div>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Card className="text-center">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center mx-auto mb-3">
              <SafeIcon icon={FiUsers} className="text-white text-xl" />
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {analytics?.active_children || 0}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">Active Children</div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              In your family
            </div>
          </Card>
        </motion.div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Performer */}
        <Card>
          <div className="flex items-center gap-3 mb-4">
            <SafeIcon icon={FiAward} className="text-yellow-500 dark:text-yellow-400 text-xl" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Top Performer</h3>
          </div>

          {topPerformer ? (
            <div className="flex items-center gap-4">
              <div className="text-4xl">{topPerformer.avatar || '👦'}</div>
              <div className="flex-1">
                <h4 className="font-semibold text-gray-900 dark:text-gray-100">{topPerformer.name}</h4>
                <p className="text-gray-600 dark:text-gray-400">{topPerformer.points} total points</p>
                <div className="mt-2">
                  <ProgressBar 
                    value={topPerformer.points} 
                    max={Math.max(topPerformer.points, 100)} 
                    color="primary" 
                    size="sm" 
                  />
                </div>
              </div>
              <Badge variant="success" className="flex items-center gap-1">
                <SafeIcon icon={FiStar} className="text-xs" />
                #1
              </Badge>
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="text-4xl mb-3">🏆</div>
              <p className="text-gray-500 dark:text-gray-400">No activity yet</p>
              <p className="text-sm text-gray-400 dark:text-gray-500">Complete some tasks to see your top performer!</p>
            </div>
          )}
        </Card>

        {/* Completion Trend */}
        <Card>
          <div className="flex items-center gap-3 mb-4">
            <SafeIcon icon={FiActivity} className="text-blue-500 dark:text-blue-400 text-xl" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">7-Day Activity</h3>
          </div>
          <div className="space-y-2">
            {completionTrend.map((day, index) => {
              const maxCount = Math.max(...completionTrend.map(d => d.count), 1)
              return (
                <div key={index} className="flex items-center gap-3">
                  <div className="text-xs text-gray-500 dark:text-gray-400 w-12">
                    {day.dayName}
                  </div>
                  <div className="flex-1">
                    <ProgressBar 
                      value={day.count} 
                      max={maxCount} 
                      showLabel={false} 
                      color="secondary" 
                      size="sm" 
                    />
                  </div>
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100 w-8 text-right">
                    {day.count}
                  </div>
                </div>
              )
            })}
          </div>
        </Card>

        {/* Category Breakdown */}
        <Card>
          <div className="flex items-center gap-3 mb-4">
            <SafeIcon icon={FiBarChart3} className="text-green-500 dark:text-green-400 text-xl" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Popular Categories</h3>
          </div>

          {categoryBreakdown.length > 0 ? (
            <div className="space-y-3">
              {categoryBreakdown.slice(0, 5).map((item, index) => {
                const maxCount = Math.max(...categoryBreakdown.map(c => c.count), 1)
                return (
                  <div key={item.category} className="flex items-center gap-3">
                    <div className="text-lg">
                      {item.category === 'cleaning' && '🧹'}
                      {item.category === 'dishes' && '🍽️'}
                      {item.category === 'laundry' && '👕'}
                      {item.category === 'pets' && '🐕'}
                      {item.category === 'homework' && '📚'}
                      {item.category === 'outdoor' && '🌱'}
                      {item.category === 'cooking' && '👨‍🍳'}
                      {item.category === 'organization' && '📦'}
                      {!['cleaning', 'dishes', 'laundry', 'pets', 'homework', 'outdoor', 'cooking', 'organization'].includes(item.category) && '⭐'}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium capitalize text-gray-900 dark:text-gray-100">{item.category}</span>
                        <span className="text-sm text-gray-500 dark:text-gray-400">{item.count}</span>
                      </div>
                      <ProgressBar 
                        value={item.count} 
                        max={maxCount} 
                        showLabel={false} 
                        color="highlight" 
                        size="sm" 
                      />
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="text-4xl mb-3">📊</div>
              <p className="text-gray-500 dark:text-gray-400">No category data</p>
              <p className="text-sm text-gray-400 dark:text-gray-500">Complete some tasks to see category trends!</p>
            </div>
          )}
        </Card>

        {/* Children Performance */}
        <Card>
          <div className="flex items-center gap-3 mb-4">
            <SafeIcon icon={FiUsers} className="text-purple-500 dark:text-purple-400 text-xl" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Children Performance</h3>
          </div>

          {childrenAnalytics.length > 0 ? (
            <div className="space-y-4">
              {childrenAnalytics.map((child) => {
                const maxPoints = Math.max(...childrenAnalytics.map(c => c.totalPoints), 1)
                return (
                  <div key={child.id} className="flex items-center gap-3">
                    <div className="text-2xl">{child.avatar || '👦'}</div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-gray-900 dark:text-gray-100">{child.name}</span>
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {child.totalCompletions} tasks
                        </span>
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                        {child.totalPoints} points • Favorite: {child.favoriteCategory}
                      </div>
                      <ProgressBar 
                        value={child.totalPoints} 
                        max={maxPoints} 
                        showLabel={false} 
                        color="accent" 
                        size="sm" 
                      />
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="text-4xl mb-3">👨‍👩‍👧‍👦</div>
              <p className="text-gray-500 dark:text-gray-400">No children added yet</p>
              <p className="text-sm text-gray-400 dark:text-gray-500">Add children to see their performance!</p>
            </div>
          )}
        </Card>
      </div>

      {/* Insights */}
      <Card>
        <div className="flex items-center gap-3 mb-4">
          <SafeIcon icon={FiTrendingUp} className="text-blue-500 dark:text-blue-400 text-xl" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Insights & Tips</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2">🎯 Progress Status</h4>
            <p className="text-sm text-blue-700 dark:text-blue-300">
              {analytics?.has_completions 
                ? `Great job! Your family has completed ${analytics.lifetime_tasks_completed} tasks total and earned ${analytics.lifetime_points_earned} points. ${analytics.has_recent_activity ? `${analytics.total_tasks_completed} completed in the selected timeframe.` : 'Keep up the momentum!'}`
                : "Create some tasks and start completing them to see detailed analytics!"
              }
            </p>
          </div>

          <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
            <h4 className="font-medium text-green-800 dark:text-green-200 mb-2">⭐ Top Category</h4>
            <p className="text-sm text-green-700 dark:text-green-300">
              {categoryBreakdown.length > 0 
                ? `${categoryBreakdown[0].category} tasks are most popular with ${categoryBreakdown[0].count} completions!`
                : "Complete tasks in different categories to see which ones are most popular!"
              }
            </p>
          </div>

          <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
            <h4 className="font-medium text-purple-800 dark:text-purple-200 mb-2">📈 Family Growth</h4>
            <p className="text-sm text-purple-700 dark:text-purple-300">
              {analytics?.active_children > 0 
                ? `You have ${analytics.active_children} active children. Consider adding variety to keep everyone engaged!`
                : "Add children to your family to start tracking individual progress and achievements!"
              }
            </p>
          </div>

          <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
            <h4 className="font-medium text-yellow-800 dark:text-yellow-200 mb-2">🏆 Motivation Tip</h4>
            <p className="text-sm text-yellow-700 dark:text-yellow-300">
              {topPerformer 
                ? `${topPerformer.name} is leading with ${topPerformer.points} points! Consider setting up family challenges.`
                : "Set up rewards and celebrations to make chore completion more exciting for everyone!"
              }
            </p>
          </div>
        </div>
      </Card>
    </div>
  )
}

export default AnalyticsView